import React, { useEffect, useState, useRef, Children } from 'react';
import { motion, useInView } from 'framer-motion';
import { StarIcon, QuoteIcon } from 'lucide-react';
const cases = [{
  company: 'Veldhuis & Bremmer Finance',
  sector: 'Financieel advies · Noord-Holland',
  author: 'Mark Veldhuis, mede-eigenaar',
  result: '+340% meer aanvragen',
  period: '4 maanden na DataSprint',
  quote: 'We dachten jarenlang te weten wat onze klanten zochten. Het CorePrint-rapport liet zien dat we er 60% naast zaten. Nu converteert onze website eindelijk zoals het hoort.',
  metric1: {
    val: 340,
    suffix: '%',
    label: 'meer aanvragen'
  },
  metric2: {
    val: 68,
    suffix: '%',
    label: 'lagere kosten per lead',
    prefix: '−'
  }
}, {
  company: 'Kragt Recruitment',
  sector: 'HR & werving · Utrecht',
  author: 'Sanne Kragt, founder',
  result: '6× ROI in 5 maanden',
  period: '5 maanden na start',
  quote: 'Eindelijk een bureau dat niet begint met een nieuwe website, maar met bewijs. Het verschil in resultaat is dag en nacht. We weten nu precies waar onze beste kandidaten en klanten vandaan komen.',
  metric1: {
    val: 6,
    suffix: '×',
    label: 'ROI op investering'
  },
  metric2: {
    val: 55,
    suffix: '%',
    label: 'minder ad-budget nodig',
    prefix: '−'
  }
}];
function AnimatedMetric({
  val,
  suffix = '',
  prefix = ''
}: {
  val: number;
  suffix?: string;
  prefix?: string;
}) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (!inView) return;
    const duration = 2000;
    const steps = 60;
    const increment = val / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= val) {
        setCount(val);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [inView, val]);
  return <span ref={ref} data-id="element-336">
      {prefix}
      {count}
      {suffix}
    </span>;
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 50,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 15
    }
  }
};
export function PoC() {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="bewijs" ref={ref} className="py-20 bg-white" data-id="element-337">
      <div className="max-w-[1200px] mx-auto px-6" data-id="element-338">
        <motion.div className="text-center mb-14" initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        type: 'spring',
        stiffness: 100
      }} data-id="element-339">
          <div className="section-label" data-id="element-340">Bewijs</div>
          <h2 className="text-[clamp(2.2rem,4vw,3.2rem)] font-black text-brand-dark leading-tight tracking-tight" data-id="element-341">
            Resultaten die spreken
          </h2>
          <p className="text-brand-gray mt-5 max-w-[520px] mx-auto text-[1.05rem]" data-id="element-342">
            Geen mooie verhalen. Echte data van echte klanten.
          </p>
        </motion.div>

        <motion.div className="grid md:grid-cols-2 gap-8" variants={containerVariants} initial="hidden" animate={inView ? 'visible' : 'hidden'} data-id="element-343">
          {cases.map(c => <motion.div key={c.company} className="rounded-[2rem] border border-[#E8EDF8] overflow-hidden cursor-default" variants={itemVariants} whileHover={{
          y: -6,
          boxShadow: '0 20px 60px rgba(0, 82, 204, 0.12)',
          transition: {
            type: 'spring',
            stiffness: 300,
            damping: 20
          }
        }} data-id="element-344">
              {/* Header */}
              <div className="p-9 pb-7" style={{
            background: 'linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)'
          }} data-id="element-345">
                <div className="flex justify-between items-start mb-5" data-id="element-346">
                  <div data-id="element-347">
                    <div className="font-black text-brand-dark text-lg" data-id="element-348">
                      {c.company}
                    </div>
                    <div className="text-brand-gray text-sm mt-1 font-medium" data-id="element-349">
                      {c.sector}
                    </div>
                  </div>
                  <div className="flex gap-0.5" data-id="element-350">
                    {[1, 2, 3, 4, 5].map(s => <motion.div key={s} initial={{
                  opacity: 0,
                  scale: 0
                }} animate={inView ? {
                  opacity: 1,
                  scale: 1
                } : {}} transition={{
                  delay: 0.5 + s * 0.05,
                  type: 'spring',
                  stiffness: 300
                }} data-id="element-351">
                        <StarIcon size={15} className="text-[#FEBC2E] fill-[#FEBC2E]" data-id="element-352" />
                      </motion.div>)}
                  </div>
                </div>
                <div className="flex gap-4" data-id="element-353">
                  {[c.metric1, c.metric2].map((m, i) => <motion.div key={m.label} className="flex-1 rounded-2xl p-5 bg-white border border-[#E8EDF8]" whileHover={{
                scale: 1.05,
                transition: {
                  type: 'spring',
                  stiffness: 300
                }
              }} data-id="element-354">
                      <div className="text-3xl font-black text-brand-primary" data-id="element-355">
                        <AnimatedMetric val={m.val} suffix={m.suffix} prefix={m.prefix || ''} data-id="element-356" />
                      </div>
                      <div className="text-brand-gray text-xs mt-2 font-medium" data-id="element-357">
                        {m.label}
                      </div>
                    </motion.div>)}
                </div>
              </div>
              {/* Quote */}
              <div className="p-9 pt-7 bg-white" data-id="element-358">
                <QuoteIcon size={28} className="text-brand-frost mb-4" data-id="element-359" />
                <p className="text-brand-gray italic leading-relaxed text-[0.98rem]" data-id="element-360">
                  &#8220;{c.quote}&#8221;
                </p>
                <div className="mt-6 flex items-center gap-3" data-id="element-361">
                  <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-black text-white text-sm" style={{
                background: 'linear-gradient(135deg, #0052CC, #0066FF)'
              }} data-id="element-362">
                    {c.author.split(' ').map(w => w[0]).slice(0, 2).join('')}
                  </div>
                  <div data-id="element-363">
                    <div className="text-brand-dark text-sm font-bold leading-tight" data-id="element-364">
                      {c.author}
                    </div>
                    <div className="text-brand-primary text-xs font-bold mt-0.5" data-id="element-365">
                      {c.period}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </motion.div>
      </div>
    </section>;
}